import axios from "axios";
import { getItem } from "@/utils/localStorageControl.js";
import { useToast } from "vue-toast-notification";
import store from "../store";
import { AppCodes } from "@/utils/constants";
import cookieUtils from "@/utils/Cookies";
import { logOut } from "../services/authservices";
import { useEncryption } from "@/utils/useEncryption";

const { encrypt, decrypt } = useEncryption();
// eslint-disable-next-line no-unused-vars
const appId = getItem("selectedApp") ?? AppCodes.matta;

export const API_URL = process.env.VUE_APP_URL;
export const OXIDE_API_URL = process.env.VUE_APP_OXIDE_API_URL;
export const FLUX_API_URL = process.env.VUE_APP_FLUX_API_URL;
export const ORBITAL_API_URL = process.env.VUE_APP_ORBITAL_API_URL;
export const SSO_API_URL = process.env.VUE_APP_SSO_API_URL;

export const WALLET_API_URL = process.env.VUE_APP_WALLET_API_URL;
export const GATEWAY_API_URL = process.env.VUE_APP_GATEWAY_APP_URL;
export const CURRENCY_API_URL = process.env.VUE_APP_CURRENCY_API_URL;

const ApiUrl = {
  [AppCodes.matta]: API_URL,
  [AppCodes.oxide]: OXIDE_API_URL,
  [AppCodes.orbital]: ORBITAL_API_URL,
  [AppCodes.flux]: FLUX_API_URL,
};

// eslint-disable-next-line no-unused-vars
const toast = useToast();

// To ensure refresh logic runs once
let isRefreshing = false;
let subscribers = [];

// Function to notify all queued subscribers
function onRefreshed(newAccessToken) {
  subscribers.forEach((callback) => callback(newAccessToken));
  subscribers = [];
}

// Add subscribers for token refresh
function addSubscriber(callback) {
  subscribers.push(callback);
}

// Function to create an axios instance with a specific base URL
// Add retry count tracking
let retryCount = 0;
const MAX_RETRIES = 2;

const SENSITIVE_FIELDS = [
  "email",
  "password",
  "companyEmail",
  "confirmPassword",
  "newPassword",
  "oldPassword",
];

// Encrypt selected fields
const encryptFields = (payload, encrypt) => {
  if (!payload || typeof payload !== "object") return payload;

  const data = { ...payload };

  SENSITIVE_FIELDS.forEach((key) => {
    if (data[key]) {
      data[key] = encrypt(data[key]);
    }
  });

  return data;
};

// Decrypt selected fields (recursive-safe)
const decryptFields = (payload, decrypt) => {
  if (!payload || typeof payload !== "object") return payload;

  const data = Array.isArray(payload) ? [...payload] : { ...payload };

  Object.keys(data).forEach((key) => {
    if (SENSITIVE_FIELDS.includes(key) && data[key]) {
      try {
        data[key] = decrypt(data[key]);
      } catch {
        /* ignore if not encrypted */
      }
    } else if (typeof data[key] === "object") {
      data[key] = decryptFields(data[key], decrypt);
    }
  });

  return data;
};

function createAxiosInstance(baseUrl = API_URL) {
  const token = cookieUtils.get("token");
  const axiosInstance = axios.create({
    baseURL: baseUrl ?? ApiUrl[appId],
    headers: { Authorization: `Bearer ${token}` },
  });
  axiosInstance.interceptors.request.use((config) => {
    // 🔐 Encrypt sensitive fields (skip FormData)
    if (config.data && !(config.data instanceof FormData)) {
      config.data = encryptFields(config.data, encrypt);
    }

    return config;
  });
  axiosInstance.interceptors.response.use(
    (response) => {
      // 🔓 Decrypt sensitive fields in response
      if (response?.data && typeof response.data === "object") {
        response.data = decryptFields(response.data, decrypt);
      }
      return response;
    },
    async (error) => {
      if (error.response?.status === 403) {
        if (!isRefreshing && retryCount < MAX_RETRIES) {
          isRefreshing = true;
          retryCount++;

          try {
            const refreshResponse = await axios.post(
              `${SSO_API_URL}/v1/Account/refreshtoken`,
              {
                token: store.getters.refreshToken,
                ipAddress: "string",
              }
            );

            const newAccessToken = refreshResponse.data.jwToken;
            const newRefreshToken = refreshResponse.data.refreshToken;

            store.commit("setAccessToken", newAccessToken);
            store.commit("setRefreshToken", newRefreshToken);
            axiosInstance.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;

            onRefreshed(newAccessToken);
            isRefreshing = false;

            return axiosInstance.request(error.config);
          } catch (refreshError) {
            isRefreshing = false;
            logOut();
            toast.info("Your session has expired", { position: "bottom" });
            return Promise.reject(refreshError);
          }
        } else if (retryCount >= MAX_RETRIES) {
          toast.info("Maximum retry attempts reached", { position: "bottom" });
          logOut();
          return Promise.reject(error);
        } else {
          return new Promise((resolve) => {
            addSubscriber((newAccessToken) => {
              error.config.headers[
                "Authorization"
              ] = `Bearer ${newAccessToken}`;
              resolve(axiosInstance.request(error.config));
            });
          });
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
}

// API Helper Functions

// GET Request
export async function get(url, config = {}, baseUrl = null) {
  const axiosInstance = createAxiosInstance(baseUrl);
  return await axiosInstance.get(url, config);
}
export async function walletGet(url, config = {}) {
  const axiosInstance = createAxiosInstance(WALLET_API_URL);
  return await axiosInstance.get(url, config);
}

// POST Request
export async function post(url, data, config = {}, baseUrl = null) {
  const axiosInstance = createAxiosInstance(baseUrl);
  return await axiosInstance.post(url, data, config);
}

export async function fluxGet(url, data, config = {}) {
  const axiosInstance = createAxiosInstance(FLUX_API_URL);
  return await axiosInstance.get(url, { params: data, ...config });
}
export async function fluxPost(url, data, config = {}) {
  const axiosInstance = createAxiosInstance(FLUX_API_URL);
  return await axiosInstance.post(url, data, config);
}

export async function gatewayPost(url, data, config = {}) {
  const axiosInstance = createAxiosInstance(GATEWAY_API_URL);
  return await axiosInstance.post(url, data, config);
}

// PUT Request
export async function put(url, data, config = {}, baseUrl = null) {
  const axiosInstance = createAxiosInstance(baseUrl);
  return await axiosInstance.put(url, data, config);
}

// DELETE Request
export async function del(url, config = {}, baseUrl = null) {
  const axiosInstance = createAxiosInstance(baseUrl);
  return await axiosInstance.delete(url, config);
}
